/**
 * CardShellz Reviews — Display Controller
 *
 * Renders review cards, pagination, sorting, and carousels.
 * Consumes data from window.CSZReviews (provider adapter).
 *
 * Strategy: Prefetch data immediately on page load (before scroll),
 * then render when the section scrolls into view. This hides the
 * Judge.me API latency behind normal browsing time.
 */
(function () {
  'use strict';
  if (window.CSZReviewsUI) return;

  var prefetchPromises = {};

  /* ── Utilities ── */
  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function formatDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  }

  function renderStarsSvg(rating, size) {
    size = size || 14;
    var html = '';
    for (var i = 1; i <= 5; i++) {
      var cls = rating >= (i - 0.3) ? 'csz-star--filled' : (rating >= (i - 0.7) ? 'csz-star--half' : 'csz-star--empty');
      html += '<svg class="csz-star ' + cls + '" width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>';
    }
    return html;
  }

  /* ── Review Card Renderer ── */
  function renderReviewCard(review, opts) {
    opts = opts || {};
    var showProduct = opts.showProduct || false;

    var card = document.createElement('article');
    card.className = 'csz-review';

    var headerHtml =
      '<header class="csz-review__header">' +
        '<div class="csz-review__stars">' + renderStarsSvg(review.rating, 14) + '</div>' +
        '<time class="csz-review__date" datetime="' + escapeHtml(review.createdAt) + '">' + formatDate(review.createdAt) + '</time>' +
      '</header>';

    var authorHtml =
      '<div class="csz-review__author-line">' +
        '<span class="csz-review__author">' + escapeHtml(review.author) + '</span>' +
        (review.verified ? ' <span class="csz-review__verified"><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg> Verified</span>' : '') +
      '</div>';

    var bodyHtml =
      '<div class="csz-review__body">' +
        (review.title ? '<strong class="csz-review__title">' + escapeHtml(review.title) + '</strong> ' : '') +
        escapeHtml(review.body) +
      '</div>';

    var footerParts = [];
    if (review.variant) {
      footerParts.push('<span class="csz-review__variant">Variant: ' + escapeHtml(review.variant) + '</span>');
    }
    if (showProduct && review.productTitle) {
      footerParts.push('<span class="csz-review__product">' + escapeHtml(review.productTitle) + '</span>');
    }
    var footerHtml = footerParts.length > 0 ? '<footer class="csz-review__footer">' + footerParts.join('') + '</footer>' : '';

    var imagesHtml = '';
    if (review.images && review.images.length > 0) {
      imagesHtml = '<div class="csz-review__images">';
      review.images.forEach(function (url) {
        imagesHtml += '<img class="csz-review__image" src="' + escapeHtml(url) + '" alt="Review photo" loading="lazy" width="48" height="48">';
      });
      imagesHtml += '</div>';
    }

    // Order: header (stars/date) → body (title+text) → images → author-line (footer) → footer
    card.innerHTML = headerHtml + bodyHtml + imagesHtml + authorHtml + footerHtml;
    return card;
  }

  /* ── Skeleton Loader ── */
  function renderSkeleton(count) {
    var frag = document.createDocumentFragment();
    for (var i = 0; i < count; i++) {
      var el = document.createElement('article');
      el.className = 'csz-review csz-review--skeleton';
      el.innerHTML =
        '<div class="csz-review__skeleton-stars"></div>' +
        '<div class="csz-review__skeleton-text" style="width:80%"></div>' +
        '<div class="csz-review__skeleton-text" style="width:60%"></div>' +
        '<div class="csz-review__skeleton-text" style="width:40%"></div>';
      frag.appendChild(el);
    }
    return frag;
  }

  /* ── Distribution Bars ── */
  function renderDistribution(distribution, total) {
    if (!distribution || !total) return '';
    var html = '<div class="csz-review-summary__distribution">';
    for (var i = 0; i < 5; i++) {
      var stars = 5 - i;
      var count = distribution[i] || 0;
      var pct = total > 0 ? Math.round((count / total) * 100) : 0;
      html +=
        '<div class="csz-review-summary__bar-row">' +
          '<span class="csz-review-summary__bar-label">' + stars + '</span>' +
          '<div class="csz-review-summary__bar"><div class="csz-review-summary__bar-fill" style="width:' + pct + '%"></div></div>' +
          '<span class="csz-review-summary__bar-count">' + count + '</span>' +
        '</div>';
    }
    html += '</div>';
    return html;
  }

  /* ── Sort Dropdown ── */
  function renderSortDropdown(current) {
    var options = [
      { value: 'newest', label: 'Newest' },
      { value: 'highest', label: 'Highest Rating' },
      { value: 'lowest', label: 'Lowest Rating' }
    ];
    var html = '<select class="csz-reviews__sort" aria-label="Sort reviews">';
    options.forEach(function (opt) {
      html += '<option value="' + opt.value + '"' + (current === opt.value ? ' selected' : '') + '>' + opt.label + '</option>';
    });
    html += '</select>';
    return html;
  }

  /* ── Prefetch: start API calls immediately, before user scrolls ── */
  function prefetchAll() {
    if (!window.CSZReviews) return;

    document.querySelectorAll('[data-csz-reviews="section"]').forEach(function (el) {
      var productId = el.getAttribute('data-product-id');
      var productHandle = el.getAttribute('data-product-handle') || '';
      var mode = el.getAttribute('data-mode') || 'product';
      var perPage = parseInt(el.getAttribute('data-per-page'), 10) || 5;
      var defaultSort = el.getAttribute('data-sort') || 'newest';

      var key = (productId || productHandle || 'all') + ':' + mode;

      if (mode === 'all') {
        prefetchPromises[key] = CSZReviews.fetchAllReviews({ page: 1, perPage: perPage, sort: defaultSort });
      } else if (productId || productHandle) {
        prefetchPromises[key] = CSZReviews.fetchProductReviews(productId, { page: 1, perPage: perPage, sort: defaultSort, handle: productHandle });
      }
    });

    /* Also prefetch carousels */
    document.querySelectorAll('[data-csz-reviews="carousel"]').forEach(function (el) {
      var limit = parseInt(el.getAttribute('data-limit'), 10) || 6;
      prefetchPromises['carousel'] = CSZReviews.fetchFeaturedReviews({ limit: limit });
    });
  }

  /* ── Main Section Initializer ── */
  function initReviewSection(container) {
    var productId = container.getAttribute('data-product-id');
    var productHandle = container.getAttribute('data-product-handle') || '';
    var mode = container.getAttribute('data-mode') || 'product';
    var perPage = parseInt(container.getAttribute('data-per-page'), 10) || 5;
    var defaultSort = container.getAttribute('data-sort') || 'newest';
    var showSummary = container.getAttribute('data-show-summary') !== 'false';
    var showWriteReview = container.getAttribute('data-show-write-review') !== 'false';
    var productUrl = container.getAttribute('data-product-url') || '';

    if (!window.CSZReviews) {
      container.innerHTML = '<p class="csz-reviews__empty">Reviews unavailable.</p>';
      return;
    }

    var currentPage = 1;
    var currentSort = defaultSort;
    var totalPages = 1;

    var summaryEl = container.querySelector('.csz-review-summary');
    var listEl = container.querySelector('.csz-reviews__list');
    var controlsEl = container.querySelector('.csz-reviews__controls');

    if (!listEl) {
      listEl = document.createElement('div');
      listEl.className = 'csz-reviews__list';
      container.appendChild(listEl);
    }

    function buildControls() {
      // Sort is injected into the summary box after data loads (in loadReviews callback).
      // Write a Review is in the section header Liquid — not injected here.
      // Nothing to do in buildControls for the new layout.
    }

    function loadReviews(replace) {
      if (replace) {
        listEl.innerHTML = '';
        listEl.appendChild(renderSkeleton(perPage));
      }

      /* Use prefetched promise if available (page 1 with default sort) */
      var prefetchKey = (productId || productHandle || 'all') + ':' + mode;
      var fetchPromise;

      if (currentPage === 1 && currentSort === defaultSort && prefetchPromises[prefetchKey]) {
        fetchPromise = prefetchPromises[prefetchKey];
        delete prefetchPromises[prefetchKey]; /* Use only once */
      } else if (mode === 'all') {
        fetchPromise = CSZReviews.fetchAllReviews({ page: currentPage, perPage: perPage, sort: currentSort });
      } else {
        fetchPromise = CSZReviews.fetchProductReviews(productId, { page: currentPage, perPage: perPage, sort: currentSort, handle: productHandle });
      }

      fetchPromise.then(function (data) {
        if (replace) listEl.innerHTML = '';

        var reviews = data.reviews || [];
        totalPages = data.totalPages || 1;

        if (reviews.length === 0 && currentPage === 1) {
          if (data._noToken) {
            listEl.innerHTML = '<p class="csz-reviews__empty csz-reviews__config-msg">Reviews require API token. Add your Judge.me Public Token in Theme Settings &rarr; CardShellz Reviews.</p>';
          } else {
            listEl.innerHTML = '<p class="csz-reviews__empty">No reviews yet. Be the first to share your experience!</p>';
          }
          /* removeLoadMore() was undefined here — caused ReferenceError that
             fell through to the .catch and rendered "Could not load reviews."
             instead of the proper empty / token-warning message. Pagination
             cleanup happens via updatePagination() when totalPages <= 1. */
          return;
        }

        var showProduct = mode === 'all';
        reviews.forEach(function (review) {
          listEl.appendChild(renderReviewCard(review, { showProduct: showProduct }));
        });

        if (showSummary && summaryEl && data.average) {
          var pctFiveStar = data.distribution && data.count > 0
            ? Math.round((data.distribution[5] || 0) / data.count * 100)
            : null;
          var countLabel = (data.count || 0) + ' reviews' + (pctFiveStar ? ' &middot; ' + pctFiveStar + '% five star' : '');
          var writeLink = showWriteReview
            ? '<a href="' + escapeHtml(productUrl) + '#judgeme_product_reviews_header" class="csz-reviews__write-link">Write a Review</a>'
            : '';
          summaryEl.innerHTML =
            '<div class="csz-review-summary__inner">' +
              '<div class="csz-review-summary__left">' +
                '<span class="csz-review-summary__average">' + data.average.toFixed(1) + '</span>' +
                '<div class="csz-review-summary__stars">' + renderStarsSvg(data.average, 16) + '</div>' +
                '<span class="csz-review-summary__count">' + countLabel + '</span>' +
              '</div>' +
            '</div>' +
            '<div class="csz-reviews__sort-wrap">' + renderSortDropdown(currentSort) + writeLink + '</div>';

          summaryEl.querySelector('.csz-reviews__sort').addEventListener('change', function (e) {
            currentSort = e.target.value;
            currentPage = 1;
            loadReviews(true);
          });
        }

        updatePagination();
      }).catch(function (err) {
        console.error('[CSZReviews] Fetch error:', err);
        if (replace) listEl.innerHTML = '<p class="csz-reviews__empty">Could not load reviews.</p>';
      });
    }

    var paginationEl = null;

    function updatePagination() {
      if (totalPages <= 1) {
        if (paginationEl && paginationEl.parentNode) paginationEl.parentNode.removeChild(paginationEl);
        paginationEl = null;
        return;
      }
      if (!paginationEl) {
        paginationEl = document.createElement('div');
        paginationEl.className = 'csz-reviews__pagination';
        container.appendChild(paginationEl);
      }

      var html = '';
      // Prev button
      if (currentPage > 1) {
        html += '<button class="csz-reviews__page-btn" data-page="' + (currentPage - 1) + '">&larr;</button>';
      }
      // Page numbers — show up to 5 around current page
      var start = Math.max(1, currentPage - 2);
      var end   = Math.min(totalPages, currentPage + 2);
      if (start > 1) html += '<button class="csz-reviews__page-btn" data-page="1">1</button>';
      if (start > 2) html += '<span class="csz-reviews__page-ellipsis">&hellip;</span>';
      for (var p = start; p <= end; p++) {
        html += '<button class="csz-reviews__page-btn' + (p === currentPage ? ' csz-reviews__page-btn--active' : '') + '" data-page="' + p + '">' + p + '</button>';
      }
      if (end < totalPages - 1) html += '<span class="csz-reviews__page-ellipsis">&hellip;</span>';
      if (end < totalPages) html += '<button class="csz-reviews__page-btn" data-page="' + totalPages + '">' + totalPages + '</button>';
      // Next button
      if (currentPage < totalPages) {
        html += '<button class="csz-reviews__page-btn" data-page="' + (currentPage + 1) + '">&rarr;</button>';
      }

      paginationEl.innerHTML = html;
      paginationEl.querySelectorAll('[data-page]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          currentPage = parseInt(btn.getAttribute('data-page'), 10);
          loadReviews(true);
          // Scroll to reviews section top
          container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    }

    buildControls();
    loadReviews(true);
  }

  /* ── Carousel Initializer ── */
  function initCarousel(container) {
    var limit = parseInt(container.getAttribute('data-limit'), 10) || 6;

    if (!window.CSZReviews) return;

    var trackEl = container.querySelector('.csz-reviews-carousel__track');
    if (!trackEl) {
      trackEl = document.createElement('div');
      trackEl.className = 'csz-reviews-carousel__track';
      container.appendChild(trackEl);
    }

    trackEl.appendChild(renderSkeleton(limit));

    var fetchPromise = prefetchPromises['carousel'] || CSZReviews.fetchFeaturedReviews({ limit: limit });
    delete prefetchPromises['carousel'];

    fetchPromise.then(function (reviews) {
      trackEl.innerHTML = '';
      if (reviews.length === 0) {
        trackEl.innerHTML = '<p class="csz-reviews__empty">No reviews yet.</p>';
        return;
      }
      reviews.forEach(function (review) {
        var card = renderReviewCard(review, { showProduct: true });
        card.classList.add('csz-reviews-carousel__card');
        trackEl.appendChild(card);
      });

      initCarouselNav(container, trackEl);
    });
  }

  function initCarouselNav(container, track) {
    var prevBtn = container.querySelector('.csz-reviews-carousel__prev');
    var nextBtn = container.querySelector('.csz-reviews-carousel__next');
    if (!prevBtn || !nextBtn) return;

    var scrollAmount = 340;
    prevBtn.addEventListener('click', function () {
      track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    });
    nextBtn.addEventListener('click', function () {
      track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    });
  }

  /* ── Init: prefetch immediately, render on scroll ── */
  function initElement(el) {
    if (el.getAttribute('data-csz-initialized')) return;
    el.setAttribute('data-csz-initialized', 'true');

    var type = el.getAttribute('data-csz-reviews');
    if (type === 'carousel') {
      initCarousel(el);
    } else {
      initReviewSection(el);
    }
  }

  function init() {
    /* Step 1: Start API calls immediately (before scroll) */
    prefetchAll();

    /* Step 2: Render when section scrolls into view */
    var elements = document.querySelectorAll('[data-csz-reviews]');
    if (!elements.length) return;

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            observer.unobserve(entry.target);
            initElement(entry.target);
          }
        });
      }, { rootMargin: '400px' });

      elements.forEach(function (el) {
        if (el.getAttribute('data-csz-initialized')) return;
        observer.observe(el);
      });
    } else {
      elements.forEach(initElement);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── Shopify theme editor re-render handler ───────────────────────
     When the operator changes a setting in the theme editor (e.g.
     drags the "reviews per page" slider), Shopify replaces the
     section's HTML in place. The IIFE doesn't re-execute, so without
     this listener, init() never runs against the new DOM and the
     section sits empty forever ("Could not load reviews" or just blank).

     shopify:section:load fires with e.target as the section element.
     We re-prefetch (in case settings changed perPage / sort / mode)
     and immediately init any [data-csz-reviews] descendants — no
     IntersectionObserver delay since the operator is actively editing
     this section and expects to see results.

     prefetchPromises cache is keyed only on (productId|handle|'all'):mode
     and doesn't include perPage, so it can return stale data when
     perPage changes. Clear it on every editor re-render to force a
     fresh fetch with the new settings.
     ─────────────────────────────────────────────────────────────── */
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target) return;
    var newEls = e.target.querySelectorAll('[data-csz-reviews]');
    if (!newEls.length) return;
    /* Reset prefetch cache so changed settings (perPage, sort, mode)
       trigger a fresh fetch instead of reusing the old promise. */
    prefetchPromises = {};
    /* Kick off fresh prefetch + immediate init on the new markup */
    prefetchAll();
    newEls.forEach(initElement);
  });

  /* Also re-init on section:unload → section:load cycles (when the
     section is removed and re-added). The unload event removes data-
     csz-initialized from the DOM automatically since the DOM is gone. */

  window.CSZReviewsUI = { init: init };
})();
